<template>
  <b-container fluid>
    <b-col v-if="!files.length" cols="12" class="py-5 text-center">
      <h3 class="font-weight-bold">This lot is vacant</h3>
      <p class="text-secondary">You have no files yet.</p>
      <b-img fluid src="/img/empty-req.svg" alt="Empty File Requests"></b-img>
    </b-col>
    <b-row v-else class="p-0 file-list-row">
      <div class="card-container" v-for="file in files" :key="file.id">
        <file-item
          :file="file"
          :resolve-creator-name="resolveCreatorNameFromList"
          :selectedFiles="selectedFiles"
          :selectable="selectable"
          :is-requests-files-page="isRequestsFilesPage"
          :shared-view="sharedView"
          :can-reshare="canReshare"
          :share-recipient-email="file.shared_to ? file.shared_to.email : null"
          @clearFilesSelected="clearFilesSelected"
          @checkFile="checkFile"
          @uncheckFile="uncheckFile"
          @shareFile="shareSingleFile(file)"
          @deletedFile="deletedFile(file)"
        />
      </div>
    </b-row>

    <b-alert
      dismissible
      fade
      class="custom-alert"
      :show="dismissCountDown"
      :variant="alertType"
      @dismissed="clearAlert"
      @dismiss-count-down="dlCountDownChanged"
    >
      {{ alertMsg }}
    </b-alert>
  </b-container>
</template>

<script>
import FileItem from '@/components/cards/FileItem'
import { buildCreatorNameResolverFromFiles } from '@/utils/fileLabels'

export default {
  components: {
    FileItem,
  },

  props: {
    files: {
      type: Array,
      required: true,
      default() {
        return []
      },
    },
    isRequestsFilesPage: {
      type: Boolean,
      default: false,
    },
    /** When false, per-card selection checkboxes are hidden (view-only access). */
    selectable: {
      type: Boolean,
      default: true,
    },
    /** When true, per-card options are limited to Download (you don't own these files). */
    sharedView: {
      type: Boolean,
      default: false,
    },
    /** When true (with sharedView), Share stays available so a received file can be passed on. */
    canReshare: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedFiles: [],
      dismissCountDown: 0,
      alertMsg: null,
      alertType: 'primary',
    }
  },

  computed: {
    resolveCreatorNameFromList() {
      return buildCreatorNameResolverFromFiles(this.files)
    },
  },

  methods: {
    selectAllFiles() {
      this.selectedFiles = this.files.map((file) => file.id)
      this.$emit('selectedFiles', this.selectedFiles)
    },
    /** Sync visible cards when parent selects IDs that span multiple pages (no emit). */
    syncSelectedIds(ids) {
      const allowed = new Set(Array.isArray(ids) ? ids : [])
      this.selectedFiles = this.files
        .map((file) => file.id)
        .filter((id) => allowed.has(id))
    },
    clearFilesSelected() {
      this.selectedFiles = []
    },
    async downloadSelectedFiles() {
      this.loading = true

      await this.$api
        .get('/download-bulk-files', {
          params: {
            files: this.selectedFiles,
          },
        })
        .then((response) => {
          this.loading = false
          this.clearFilesSelected()

          this.alertMsg = 'Requested download file successful!'
          this.dismissCountDown = 5
        })
        .catch((e) => {
          this.loading = false

          setTimeout((e) => {
            this.clearAlert()
          }, 5000)

          this.alertType = 'danger'
          this.alertMsg =
            'Something went wrong while downloading selected files!'
          this.dismissCountDown = 5
        })
    },
    dlCountDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    clearAlert() {
      this.dismissCountDown = 0
      this.alertMsg = null
      this.alertType = 'primary'
    },
    checkFile(file) {
      const index = this.selectedFiles.findIndex((f) => f === file)

      if (index < 0) {
        this.selectedFiles.push(file)
      }

      this.$emit('selectedFiles', this.selectedFiles)
    },
    uncheckFile(file) {
      const index = this.selectedFiles.findIndex((f) => f === file)

      if (index >= 0) {
        this.selectedFiles.splice(index, 1)
        this.$emit('selectedFiles', this.selectedFiles)
      }
    },
    shareSingleFile(file) {
      this.clearFilesSelected()
      this.checkFile(file.id)
      this.$emit('shareSingleFile', file)
    },
    deletedFile(file) {
      this.$emit('reloadFileList')
    },
  },
}
</script>

<style lang="scss" scoped>
.file-list-row {
  // Mobile: stack items vertically, no horizontal scroll
  flex-direction: column;

  @media (min-width: 768px) {
    // Desktop: horizontal scroll with cards
    flex-direction: row;
    flex-wrap: nowrap;
    min-height: 280px;
    align-items: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

.card-container {
  // Mobile: full width, no side margins
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    margin: 0 0.5rem;
    margin-bottom: 1.5rem;

    &:first-child {
      margin-left: 0 !important;
      margin-right: 0.5rem !important;
    }

    &:last-child {
      margin-left: 0.5rem !important;
      margin-right: 0 !important;
    }
  }
}
</style>
